import React from 'react';
import { PERMISSIONS } from 'constants/permissions';
import { BranchesOutlined } from '@ant-design/icons';
import { Typography, Button, Row, Col, List } from 'components';
import { Form, Select, LanguageSelect, IconWrapper } from 'components';
import { CloseIcon, CloseIconLight, DoneIcon } from 'components/common/icons';
import { useAccessControl } from 'components/common/accessControl/useAccessControl';
import { SupplierStatusBadge } from 'components/common/StatusBadge/SupplierStatusBadge';
import { SupplierItem } from '../hooks';
import styles from '../styles.module.scss';

const { Title, Text } = Typography;
const { Option } = Select;

interface SupplierSectionProps {
  form: any;
  suppliers: SupplierItem[];
  supplierOptions: any[];
  filteredSuppliers: any[];
  contactPersons: any[];
  loadingContactPersons: boolean;
  selectedSupplierId: string;
  disabledSuppActions: boolean;
  handleSupplierChange: (value: string) => void;
  handleContactPersonChange: (value: string) => void;
  handleAddSupplier: () => Promise<void>;
  handleRemoveSupplier: (index: number) => SupplierItem[];
  resetForm: () => void;
  setOpenSupplierModal: (open: boolean) => void;
  setOpenContactPersonModal: (supplierId: string) => void;
}

export const SupplierSection: React.FC<SupplierSectionProps> = ({
  form,
  suppliers,
  supplierOptions,
  filteredSuppliers,
  contactPersons,
  loadingContactPersons,
  selectedSupplierId,
  disabledSuppActions,
  handleSupplierChange,
  handleContactPersonChange,
  handleAddSupplier,
  handleRemoveSupplier,
  resetForm,
  setOpenSupplierModal,
  setOpenContactPersonModal,
}) => {
  const { hasAccess } = useAccessControl();

  return (
    <>
      <Title level={5} className={styles.sectionTitle}>
        Supplier
      </Title>

      {suppliers.length > 0 && (
        <List
          dataSource={suppliers}
          style={{ marginBottom: 24 }}
          renderItem={(item, index) => (
            <List.Item
              className={styles.list_item}
              actions={[
                <Button
                  key={'add'}
                  size={'small'}
                  type={'link'}
                  icon={<CloseIconLight />}
                  onClick={() => handleRemoveSupplier(index)}
                />,
              ]}
            >
              <Row wrap={false} gutter={5}>
                <Col>
                  <Text ellipsis={{ tooltip: true }} className={styles.option_tag}>
                    {item.supplierName}
                  </Text>
                </Col>
                <Col>
                  <Text ellipsis={{ tooltip: true }} type={'secondary'}>
                    {item.contactPersonName}
                  </Text>
                </Col>
                <Col>
                  <Text>
                    <IconWrapper src={item?.flagLocation} />
                  </Text>
                </Col>
              </Row>
            </List.Item>
          )}
        />
      )}

      <Row gutter={[16, 16]} className={styles.addSupplierRow}>
        <Col span={24}>
          <Form layout="vertical" form={form}>
            <Row gutter={[16, 16]}>
              <Col span={10}>
                <Form.Item label="Supplier" name="supplierId">
                  <Select
                    showAdd
                    optionLabelProp="label"
                    onAdd={() => setOpenSupplierModal(true)}
                    onChange={handleSupplierChange}
                  >
                    {supplierOptions.map((opt: Record<string, any>) => (
                      <Option key={opt.id} value={opt.value} label={opt.label}>
                        <Row justify={'space-between'}>
                          <Col span={14}>
                            <Text ellipsis={{ tooltip: true }}>
                              <BranchesOutlined /> {opt.label}
                            </Text>
                          </Col>
                          <Col flex={'none'} style={{ textAlign: 'right' }}>
                            {opt.status !== 'ACTIVE' ? <SupplierStatusBadge type={opt.status} /> : ''}
                          </Col>
                        </Row>
                      </Option>
                    ))}
                    {supplierOptions.length && (
                      <Option
                        disabled
                        style={{
                          borderTop: '1px solid #e8e8e8',
                          marginTop: 10,
                          marginBottom: -18,
                          height: 1,
                          padding: 0,
                        }}
                      >
                        <span />
                      </Option>
                    )}
                    {filteredSuppliers.map((opt: Record<string, any>) => (
                      <Option key={opt.id} value={opt.value} label={opt.label}>
                        <Row justify={'space-between'}>
                          <Col span={13}>
                            <Text ellipsis={{ tooltip: true }}>{opt.label}</Text>
                          </Col>
                          <Col flex={'none'} style={{ textAlign: 'right' }}>
                            {opt.status !== 'ACTIVE' ? <SupplierStatusBadge type={opt.status} /> : ''}
                          </Col>
                        </Row>
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={7}>
                <Form.Item label="Contact Person" name="contactPersonId">
                  <Select
                    onChange={handleContactPersonChange}
                    loading={loadingContactPersons}
                    onAdd={() => setOpenContactPersonModal(form.getFieldValue('supplierId'))}
                    showAdd={hasAccess(PERMISSIONS.clients.contact_person.write)}
                    options={contactPersons}
                  />
                </Form.Item>
              </Col>
              <Col span={7}>
                <Form.Item label="Languages" name="languageId">
                  <LanguageSelect disabled={!selectedSupplierId} />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Row gutter={4} justify={'end'}>
                  <Col>
                    <Button
                      disabled={disabledSuppActions}
                      color="default"
                      variant="filled"
                      onClick={resetForm}
                      icon={<CloseIcon />}
                    />
                  </Col>
                  <Col>
                    <Button
                      disabled={disabledSuppActions}
                      type="primary"
                      icon={<DoneIcon />}
                      onClick={handleAddSupplier}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
    </>
  );
};
